// mvp-returns Phase G5 — Buyer trust score client. Endpoints live on
// de-order-service: /ord/buyer-trust-scores/*.

import { HttpRequest } from "../utils";

const httpRequest = new HttpRequest();

export type TrustScoreLevel =
  | "EXCELLENT" | "GOOD" | "MODERATE" | "AT_RISK" | "HIGH_RISK";

export interface IBuyerTrustScoreSummary {
  id: string;
  businessId: string;
  // O203 — null when the buyer's name is unknown (render «-»).
  businessName: string | null;
  level: TrustScoreLevel;
  previousLevel: TrustScoreLevel | null;
  levelChangedAt: string | null;
  rawScore: number;
  returnRatePercent: number;
  disputeRatePercent: number;
  totalOrders: number;
  totalReturns: number;
  disputeCount: number;
  rejectedCount: number;
  cancelledCount: number;
  totalRefundedAmount: number;
  computedAt: string;
}

export interface IBuyerTrustScoreDetail extends IBuyerTrustScoreSummary {
  highValueReturnsCount: number;
  windowStart: string;
  windowEnd: string;
  computationVersion: number;
}

export interface ITrustScoreListParams {
  level?: TrustScoreLevel | TrustScoreLevel[];
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: "rawScore" | "returnRatePercent" | "totalReturns";
  sortDir?: "asc" | "desc";
}

export interface ITrustScoreListResponse {
  items: IBuyerTrustScoreSummary[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export const list = (params?: ITrustScoreListParams) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.get("/ord/buyer-trust-scores", params as any) as Promise<ITrustScoreListResponse>;

export const get = (businessId: string) =>
  httpRequest.get(`/ord/buyer-trust-scores/${businessId}`) as Promise<IBuyerTrustScoreDetail>;

// O206 — `businessId` limits the recompute to one buyer; without it every buyer is recomputed.
// Needs RETURN_ANALYTICS_VIEW (BE contract phase 3 §1).
export interface ITrustRecomputeResponse {
  ok: true;
  businessId: string | null;
  tenantId: string;
  upsertedCount: number;
  levelTransitions: number;
  windowStart: string;
  windowEnd: string;
}

export const recompute = (businessId?: string) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post("/ord/buyer-trust-scores/recompute", (businessId ? { businessId } : {}) as any) as Promise<ITrustRecomputeResponse>;

// mvp-returns Phase H10 — Tenant benchmark response
export interface ITrustBenchmarkResponse {
  tenant: {
    total             : number;
    averageRawScore   : number;
    medianRawScore    : number;
    averageReturnRate : number;
    averageDisputeRate: number;
    levelDistribution : Record<TrustScoreLevel, number>;
    // O204 — lower bound of each level as the tenant configured it (HIGH_RISK = below AT_RISK).
    thresholds?       : Record<Exclude<TrustScoreLevel, "HIGH_RISK">, number>;
  };
  business: {
    rawScore           : number;
    returnRatePercent  : number;
    disputeRatePercent : number;
    percentile         : number;
    rank               : number;
    total              : number;
  } | null;
}

export const benchmark = (businessId?: string) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.get("/ord/buyer-trust-scores/benchmark", (businessId ? { businessId } : {}) as any) as Promise<ITrustBenchmarkResponse>;
